import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCalendarDays, LuClapperboard, LuInfo, LuKeyboard, LuPlay, LuRotateCcw } from 'react-icons/lu';
import Button from '../../../../components/ui/Button';
import GlassIcon from '../../../../components/ui/GlassIcon';
import Modal from '../../../../components/ui/Modal';
import { useTranslation } from '../../../../i18n/LanguageContext';
import PlayerControls from './PlayerControls';
import TutorialStage from './TutorialStage';
import { CHAPTER_KEYS, beatIndexAt, buildTimeline } from './timeline';
import { formatClock, useNarration, usePlaybackClock } from './usePlayback';

/** On/off preference remembered in this browser. */
const useStoredFlag = (key, initial) => {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored === null ? initial : stored === 'true';
    } catch {
      return initial;
    }
  });
  const update = useCallback((next) => {
    setValue(next);
    try {
      window.localStorage.setItem(key, String(next));
    } catch {
      // Depolama kapalıysa yalnızca bu oturumda geçerli
    }
  }, [key]);
  return [value, update];
};

const PlayerBody = ({ onClose, onWatched }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.tutorial.player.${key}`, vars);
  const timeline = useMemo(() => buildTimeline(t), [t]);
  const { time, playing, setPlaying, rate, setRate, seek, holdRef } = usePlaybackClock(timeline);
  const narration = useNarration(language);
  const [voiceOn, setVoiceOn] = useStoredFlag('habcare.calendarTutorial.voice', true);
  const [captionsOn, setCaptionsOn] = useStoredFlag('habcare.calendarTutorial.captions', true);
  const [speakNonce, setSpeakNonce] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const spokenRef = useRef(-1);
  const watchedRef = useRef(false);

  const index = beatIndexAt(timeline.beats, time);
  const beat = timeline.beats[index];
  const chapterIndex = CHAPTER_KEYS.indexOf(beat.chapter);
  const ended = time >= timeline.total;
  const narrate = voiceOn && narration.available;

  // Opened with a click, so the video (and its voice) may start right away.
  // Focus goes to the picture so Space pauses instead of pressing the close button.
  useEffect(() => {
    setPlaying(true);
    const focusTimer = window.setTimeout(() => stageRef.current?.focus({ preventScroll: true }), 150);
    return () => window.clearTimeout(focusTimer);
  }, [setPlaying]);

  // One narration line per beat; the clock waits at the beat's end until it is spoken
  useEffect(() => {
    if (!playing || !narrate || spokenRef.current === index) return;
    spokenRef.current = index;
    holdRef.current = { index, since: 0 };
    narration.speak(beat.caption, rate, () => {
      if (holdRef.current.index === index) holdRef.current = { index: -1, since: 0 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, playing, narrate, speakNonce]);

  // Paused or muted: stop speaking; resuming repeats the current line
  useEffect(() => {
    if (playing && narrate) return;
    narration.cancel();
    holdRef.current = { index: -1, since: 0 };
    spokenRef.current = -1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, narrate]);

  useEffect(() => {
    if (!watchedRef.current && time >= timeline.total * 0.9) {
      watchedRef.current = true;
      onWatched();
    }
  }, [time, timeline.total, onWatched]);

  const jump = useCallback((value) => {
    narration.cancel();
    spokenRef.current = -1;
    seek(value);
    setSpeakNonce((nonce) => nonce + 1);
  }, [narration, seek]);

  const togglePlay = useCallback(() => {
    if (ended) {
      jump(0);
      setPlaying(true);
    } else {
      setPlaying((value) => !value);
    }
  }, [ended, jump, setPlaying]);

  const goToChapter = (target) => {
    const clamped = Math.max(0, Math.min(CHAPTER_KEYS.length - 1, target));
    jump(timeline.chapters[clamped].start);
    setPlaying(true);
  };
  // "Önceki": the start of this chapter first, then the one before
  const prevChapter = () => goToChapter(time - timeline.chapters[chapterIndex].start > 3000 ? chapterIndex : chapterIndex - 1);

  // Space plays / pauses, arrows move 5 s (buttons and the slider keep their own keys)
  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (event.key === ' ' && tag !== 'BUTTON') {
        event.preventDefault();
        togglePlay();
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        jump(time + (event.key === 'ArrowRight' ? 5000 : -5000));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, jump, time]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else frameRef.current?.requestFullscreen?.().catch(() => {});
  };

  return (
    <div className="tw-grid tw-gap-4 lg:tw-grid-cols-[minmax(0,1fr)_17rem]">
      <div ref={frameRef} className={fullscreen ? 'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-bg-black tw-p-4' : 'tw-min-w-0 tw-space-y-3'}>
        <div
          ref={stageRef}
          role="button"
          tabIndex={0}
          aria-label={ended ? label('replay') : playing ? label('pause') : label('play')}
          onClick={togglePlay}
          onKeyDown={(event) => {
            if (event.key === 'Enter') togglePlay();
          }}
          style={fullscreen ? { width: 'min(100%, calc((100vh - 12rem) * 16 / 9))' } : undefined}
          className="tw-relative tw-w-full tw-cursor-pointer tw-overflow-hidden tw-rounded-2xl tw-ring-1 tw-ring-slate-200 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500 dark:tw-ring-white/10"
        >
          <TutorialStage timeline={timeline} time={time} />
          {!playing && !ended && (
            <span className="tw-absolute tw-inset-0 tw-grid tw-place-items-center tw-bg-slate-950/25">
              <span className="tw-grid tw-h-20 tw-w-20 tw-place-items-center tw-rounded-full tw-bg-white/90 tw-text-teal-600 tw-shadow-2xl dark:tw-bg-[#262a34]/95 dark:tw-text-teal-300">
                <LuPlay className="tw-ml-1 tw-h-9 tw-w-9" />
              </span>
            </span>
          )}
          {ended && (
            <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-bg-slate-950/60 tw-p-6 tw-text-center tw-backdrop-blur-sm">
              <p className="tw-text-2xl tw-font-bold tw-text-white">{label('ended')}</p>
              <p className="tw-max-w-md tw-text-sm tw-text-white/80">{label('endedText')}</p>
              <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2" onClick={(event) => event.stopPropagation()} role="presentation">
                <Button onClick={() => { jump(0); setPlaying(true); }}>
                  <LuRotateCcw className="tw-h-4 tw-w-4" />
                  {label('replay')}
                </Button>
                <Button variant="primary" onClick={onClose}>
                  <LuCalendarDays className="tw-h-4 tw-w-4" />
                  {label('backToCalendar')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {captionsOn && (
          <div aria-live="polite" className={`tw-w-full tw-rounded-2xl tw-px-4 tw-py-3 ${fullscreen ? 'tw-bg-white/[0.06] tw-text-white' : 'tw-border tw-border-teal-500/20 tw-bg-teal-500/[0.05] dark:tw-border-teal-400/20'}`}>
            <p className={`tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] ${fullscreen ? 'tw-text-teal-300' : 'tw-text-teal-700 dark:tw-text-teal-300'}`}>
              {label('chapterOf', { current: chapterIndex + 1, total: CHAPTER_KEYS.length })} · {t(`profilePage.tutorial.chapters.${beat.chapter}`)}
            </p>
            <p className={`tw-mt-1 tw-min-h-[3rem] tw-text-base tw-font-medium tw-leading-relaxed sm:tw-text-[17px] ${fullscreen ? 'tw-text-white' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>{beat.caption}</p>
          </div>
        )}

        <PlayerControls
          timeline={timeline}
          time={time}
          playing={playing}
          ended={ended}
          rate={rate}
          captionsOn={captionsOn}
          voiceOn={voiceOn}
          voiceAvailable={narration.available}
          fullscreen={fullscreen}
          onTogglePlay={togglePlay}
          onSeek={jump}
          onPrevChapter={prevChapter}
          onNextChapter={() => goToChapter(chapterIndex + 1)}
          onRate={setRate}
          onCaptions={() => setCaptionsOn(!captionsOn)}
          onVoice={() => setVoiceOn(!voiceOn)}
          onFullscreen={toggleFullscreen}
        />
        {!narration.available && !fullscreen && (
          <p className="tw-flex tw-items-center tw-gap-1.5 tw-px-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            <LuInfo className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
            {label('voiceUnavailable')}
          </p>
        )}
      </div>

      <aside className="tw-space-y-3">
        <p className="tw-px-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{label('chapters')}</p>
        <ol className="tw-space-y-1.5">
          {timeline.chapters.map((chapter, chapterNumber) => {
            const active = chapterNumber === chapterIndex;
            const share = active ? Math.min(100, ((time - chapter.start) / (chapter.end - chapter.start)) * 100) : chapterNumber < chapterIndex ? 100 : 0;
            return (
              <li key={chapter.key}>
                <button
                  type="button"
                  onClick={() => goToChapter(chapterNumber)}
                  aria-current={active ? 'step' : undefined}
                  className={`tw-w-full tw-rounded-2xl tw-border tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
                    active
                      ? 'tw-border-teal-500/40 tw-bg-teal-500/10'
                      : 'tw-border-slate-200/80 tw-bg-white/70 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-bg-white/[0.06]'
                  }`}
                >
                  <span className="tw-flex tw-items-center tw-gap-2.5">
                    <span className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-xs tw-font-bold ${active ? 'tw-bg-teal-500 tw-text-white' : 'tw-bg-slate-100 tw-text-slate-500 dark:tw-bg-white/[0.07] dark:tw-text-slate-300'}`}>
                      {chapterNumber + 1}
                    </span>
                    <span className={`tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm ${active ? 'tw-font-semibold tw-text-teal-900 dark:tw-text-teal-100' : 'tw-font-medium tw-text-slate-700 dark:tw-text-slate-200'}`}>
                      {t(`profilePage.tutorial.chapters.${chapter.key}`)}
                    </span>
                    <span className="tw-shrink-0 tw-text-xs tw-tabular-nums tw-text-slate-400">{formatClock(chapter.end - chapter.start)}</span>
                  </span>
                  <span className="tw-mt-2 tw-block tw-h-1 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
                    <span className="tw-block tw-h-full tw-rounded-full tw-bg-teal-500" style={{ width: `${share}%` }} />
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="tw-flex tw-items-start tw-gap-2 tw-px-1 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
          <LuKeyboard className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />
          {label('keyboard')}
        </p>
        <p className="tw-flex tw-items-start tw-gap-2 tw-px-1 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
          <LuInfo className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />
          {label('demoNote')}
        </p>
      </aside>
    </div>
  );
};

PlayerBody.propTypes = { onClose: PropTypes.func.isRequired, onWatched: PropTypes.func.isRequired };

/** The calendar guide video in a dialog. Everything resets when it closes. */
const CalendarTutorialPlayer = ({ isOpen, onClose, onWatched }) => {
  const { t, language } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={t('profilePage.tutorial.player.title')}
      subtitle={t('profilePage.tutorial.player.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-4 sm:tw-p-5"
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuClapperboard aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
    >
      <PlayerBody onClose={onClose} onWatched={onWatched} />
    </Modal>
  );
};

CalendarTutorialPlayer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onWatched: PropTypes.func.isRequired,
};

export default CalendarTutorialPlayer;
